import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MessageService } from '../message.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { IJobMonitorResponse } from '../../interfaces/job-monitor-response';
import { ICommunicationHistoryResponse } from '../../interfaces/communication-history-response';
import { IJobSummary } from '../../interfaces/job-summary';
import { IJobRunner }  from '../../interfaces/job-runner';
import { IJobRegister }  from '../../interfaces/job-register';
import { IEnvironmentCount } from '../../interfaces/environment-count';
import { environment } from '../../../environments/environment';
import { SharedService } from "../../shared/services/shared.service";
import { ErrorProcessorService }  from "../../shared/services/error-processor.service";

@Injectable({
  providedIn: 'root'
})
export class JobMonitorService {

  protected baseUrl = environment.apiServer.portalUrl;

  private readonly jobsExecutionUrl = this.baseUrl + '/monitor/jobs'; 
  private readonly jobExecutionUrl = this.baseUrl + '/monitor/jobs/{jobUuid}'; 
  private readonly latestFilteredJobsUrl = this.baseUrl + '/monitor/latest-filtered-jobs'; 
  private readonly currentJobStatusUrl = this.baseUrl + '/monitor/current-job-status';
  private readonly jobCodesUrl = this.baseUrl + '/monitor/distinct-job-codes'; 
  private readonly envRegionsUrl = this.baseUrl + '/monitor/distinct-env-regions'; 
  // private jobStatusesUrl = this.baseUrl + '/monitor/distinctJobStatuses'; 
  private readonly jobSummaryUrl = this.baseUrl + '/monitor/latest-summary'; 
  private readonly environmentCountUrl = this.baseUrl + '/monitor/environment-count'; 
  private readonly taskHierarchyUrl = this.baseUrl + '/monitor/task-hierarchy'; 
  private readonly communicationHistoryUrl = this.baseUrl.replace("portal/api","communication/api") + '/admin/comm-history';

  constructor(
    private readonly http: HttpClient, 
    private readonly sharedService: SharedService,
    private readonly messageService: MessageService,
    private readonly errorProcessorService: ErrorProcessorService)
     { }

  getJobExecutions(selectedRegion: string | null, selectedBrandCode: string | null, 
    selectedJobCode: string | null, selectedJobStatus: string | null,  
    selectedDateFrom: string | null, selectedDateTo: string | null, offset: number, includeMaintenenceJobs: any):
        Observable<IJobMonitorResponse> {
    let request = {
      envRegion: selectedRegion ?? null,
      jobCode: selectedJobCode ?? null,
      brandCodes: selectedBrandCode == null ? null : [selectedBrandCode], 
      jobStatus: selectedJobStatus ?? null, 
      includeMaintenenceJobs: includeMaintenenceJobs ?? false, 
      dateFrom:  this.sharedService.getFormattedDate(selectedDateFrom) ?? null, 
      txnDateTo:  selectedDateTo != null ? this.sharedService.getFormattedDate(this.sharedService.getNextDate(selectedDateTo)) : null, 
      limit: 50,
      offset: offset
    }
    return this.http.post<IJobMonitorResponse>(this.jobsExecutionUrl, request, {responseType: 'json'})
    .pipe(
      catchError(this.errorProcessorService.handleError)
    );
  }

  getJobExecution(jobUuid: string): Observable<IJobRegister> {
    let url = this.jobExecutionUrl.replace("{jobUuid}", jobUuid);
    return this.http.get<IJobRegister>(url, {responseType: 'json'})
    .pipe(
      catchError(this.errorProcessorService.handleError)
    );
  }  

  getLatestJobs(interval: any):
      Observable<IJobRunner[]> {
      let params = new HttpParams();
    if (interval != null) {
        params = params.set('latestInterval', interval);
    }
    return this.http.get<IJobRunner[]>(this.latestFilteredJobsUrl, {params: params, responseType: 'json'})
    .pipe(
      catchError(this.errorProcessorService.handleError)
    );
  }

  getCurrentJobStatus(jobUuids: any):
      Observable<IJobRunner[]> {
      return this.http.post<IJobRunner[]>(this.currentJobStatusUrl, JSON.stringify(jobUuids), {responseType: 'json'})
      .pipe(
        catchError(this.errorProcessorService.handleError)
      );
  }

  getEnvRegions(): Observable<string[]> {
    return this.http.get<string[]>(this.envRegionsUrl, {responseType: 'json'})
    .pipe(
      catchError(this.errorProcessorService.handleError)
    );

  }

  getJobCodes(): Observable<string[]> {
    return this.http.get<string[]>(this.jobCodesUrl, {responseType: 'json'})
    .pipe(
      catchError(this.errorProcessorService.handleError)
    );

  }

  // getJobStatuses(): Observable<string[]> {
  //   return this.http.get<string[]>(this.jobStatusesUrl, {headers: this.headers, responseType: 'json'});
  // }

  getJobSummary(): Observable<[IJobSummary]> {
      let params = new HttpParams()
      .set('latestInterval', '240');
    return this.http.get<[IJobSummary]>(this.jobSummaryUrl, {params: params, responseType: 'json'})
    .pipe(
      catchError(this.errorProcessorService.handleError)
    );

  }  

  getEnvironmentCount():
        Observable<[IEnvironmentCount]> {
      let params = new HttpParams()
      .set('latestInterval', '240');
    return this.http.get<[IEnvironmentCount]>(this.environmentCountUrl, {params: params, responseType: 'json'})
    .pipe(
      catchError(this.errorProcessorService.handleError)
    );

  }  

  getTaskHierarchy():
    Observable<any[]> {
    return this.http.get<any[]>(this.taskHierarchyUrl, {responseType: 'json'})
    .pipe(
      catchError(this.errorProcessorService.handleError)
    );

  }  
  
  private log(message: string) {
//    this.messageService.add(`UserService: ${message}`);
  }

  getCommunicationHistory(selectedBrandCode: string, selectedMessageCode: string, selectedChannelCode: string,
     selectedDateFrom: string, selectedDateTo: string, offset: number):
        Observable<ICommunicationHistoryResponse> {
      let params = new HttpParams()
      .set('limit', '50')
      .set('offset', offset);
    if (selectedBrandCode) {
        params = params.set('brandCode', selectedBrandCode);
    }
    if (selectedMessageCode) {
      params = params.set('messageCode', selectedMessageCode);
    }
    if (selectedChannelCode) {
      params = params.set('channelCode', selectedChannelCode);
    }
    if (selectedDateFrom) {
      params = params.set('dateFrom', this.sharedService.getFormattedDate(selectedDateFrom));
    }
    if (selectedDateTo) {
      let dateTo = this.sharedService.getNextDate(selectedDateTo);
      params = params.set('dateTo', this.sharedService.getFormattedDate(dateTo));
    }
    return this.http.get<ICommunicationHistoryResponse>(this.communicationHistoryUrl, {params: params, responseType: 'json'})
    .pipe(
      catchError(this.errorProcessorService.handleError)
    );

  }

}
